import React, { useEffect, useState } from 'react';
import { Button, Text, View } from "../../../src/Components/Themed";
import type { AthleteProps, BestAvailableOrderProps, CompanyProps, EventOrderStatProps, EventProps, ExternalPriceProps, LeagueProps, LocationProps, MarketProps, MatchProps, MyPlayerProps, OrderProps, PlayerBalanceProps, TeamProps, TeamRankingProps, TeamRecordProps, TournamentProps, TradeProps } from '../../../src/types';
import { ActivityIndicator, FlatList, Image, Linking, Modal, Platform, StyleSheet, TouchableWithoutFeedback, useWindowDimensions } from 'react-native';
import LeagueSchedule from './LeagueSchedule';
import { MarketMakerApi } from '../../../src/MarketMaker/api';
import { MarketComponentApi } from '../../../src/MarketComponents/api';
import { MarketAppHelpers } from '../helpers';
import TeamEventMarket from '../../../src/MarketComponents/components/TeamEventMarket';
import { useColors } from '../../../src/constants/useColors';
import TeamFutureCard from './TeamFutureCard';
import { Icons, LinearDiagnal } from '../../../src/Components';
import DropDown from '../../../src/Components/Dropdown';
import SearchBox from '../../../src/Components/SearchBox';
import { MarketComponents } from 'be-components';
import MarketHeader from './MarketHeader';
import LeagueSelector from './LeagueSelector';
import OrderHolder from './OrderHolder';
import LocationHolder from '../../market/components/LocationHolder';
import PortalModal from '../../embed/components/PortalModal';
import TeamCard from './TeamCard';
import type { BEClarityProps } from '../../../src/Clarity';
const app_url = 'https://www.bettoredge.com/bettoredge-app'

type MarketAppProps = {
    me?:MyPlayerProps,
    my_balance?:PlayerBalanceProps,
    visible:boolean,
    mode:'desktop'|'mobile',
    company?:CompanyProps,
    init_event?:EventProps,
    init_league?:LeagueProps,
    init_tournament?:TournamentProps,
    init_match?:MatchProps,
    insets: { top:number, bottom:number, left:number, right:number },
    init_team?:TeamProps,
    init_athlete?:AthleteProps,
    clarity?:BEClarityProps,
    init_nav: 'league'|'team'|'athlete',
    leagues:LeagueProps[],
    onNavOut:() => void,
    markets:MarketProps[],
    event_order_stats: { [key:string]:EventOrderStatProps[] },
    best_available_orders:{ [key:string]: BestAvailableOrderProps[] },
    onRequestAuthenticate:() => void,
    onClose:() => void,
    onShowProfile:() => void,
    onShowWallet:() => void
}

type ContestStateProps = {
    loading:boolean,
    league_selector:boolean,
}

type NavigationStateProps = {
    level: 'league'|'team'|'athlete'|'event'
    league?:LeagueProps,
    division?:string,
    show_desktop_props?:boolean,
    sub_division?:string,
    team_id?:string,
    tournament?:TournamentProps,
    match?:MatchProps,
    athlete?:AthleteProps,
    event?:EventProps
    
}

const MarketApp = ({ me, my_balance, visible, mode, clarity, insets, init_tournament, init_nav, init_team, init_match, init_athlete, onNavOut, init_league, leagues, markets, company, init_event, event_order_stats, best_available_orders, onShowWallet, onRequestAuthenticate, onShowProfile, onClose }:MarketAppProps) => {
    const Colors = useColors();
    const { width, height } = useWindowDimensions();
    const embed_height = (height * 0.9) - insets.top - insets.bottom
    const embed_width = (width * 0.9) - insets.left - insets.right
    const [ nav_state, setNavState ] = useState<NavigationStateProps>({
        level: 'league'
    });

    const [ location_state, setLocation ] = useState<{
        visible: boolean,
        location?:{
            location: LocationProps,
            region:string,
            coordinates:{ latitude:number, longitude:number, [key:string]:number }
        }
    }>({
        visible:false
    })
    const [ draft_order, setDraftOrder ] = useState<{
        visible:boolean,
        order?:OrderProps,
        event?:EventProps,
        tournament?:TournamentProps,
        match?:MatchProps,
    }>({
        visible: false
    })
    const { level, league, show_desktop_props, division, sub_division, team_id, athlete, event, tournament, match } = nav_state
    const [ contest_state, setContestState ] = useState<ContestStateProps>({
        loading:false,
        league_selector:false,
    });
    const { loading, league_selector } = contest_state;
    const [ search_players, setSearchPlayers ] = useState({
        active:false,
        value:''
    });
    const [ market_data, setMarketData ] = useState<{
        market_loading:boolean,
        external_prices:ExternalPriceProps[],
        athletes:AthleteProps[],
        latest_trades:TradeProps[],
        active_player_market?:MarketProps,
    }>({
        market_loading:false,
        athletes:[],
        external_prices: [],
        latest_trades: [],
    });
    const desktop_props = width > 1200 ? true : false
    const { market_loading, latest_trades, active_player_market, athletes } = market_data;
    const ev_available_orders = best_available_orders[`team:${event?.event_id}`] ?? []
    let player_markets = markets.filter(m => event?.supported_markets?.map(sm => sm.market_id.toString()).includes(m.market_id.toString()) && m.level == 'athlete')
    let props = event ? MarketAppHelpers.getPropTradesForEvent(player_markets, latest_trades, event.event_id) : []
    let active_player_props = props.filter(p => p.market_id == active_player_market?.market_id);
    if(search_players.active){
        active_player_props = active_player_props.filter(pp => {
            let athlete = athletes.find(a => a.athlete_id == pp.side_id);
            if(!athlete){ return }
            if(`${athlete.first_name} ${athlete.last_name}`.toLowerCase().includes(search_players.value.toLowerCase())){ return pp }
            return
        })
    }
    useEffect(() => {
        if(!visible){ return }
        if(init_event){ onSelectEvent(init_event) }
    },[visible, init_event, init_league]);

    const handleDraftOrder = (o:OrderProps, e?:EventProps, t?:TournamentProps, m?:MatchProps) => {
        if(clarity){ clarity.smartEvent('draft_order') }
        switch(o.event_type){
            case 'team':
                if(!e){ return alert('Unable to process order') }
                if(clarity){ clarity.smartEvent('draft_team_order') }
                setDraftOrder({ visible: true, order:o, event: e })
                return
            case 'tournament':
                if(!t){ return alert('Unable to process order') }
                if(clarity){ clarity.smartEvent('draft_tournament_order') }
                setDraftOrder({ visible: true, order:o, tournament: t });
                return
            case 'match':
                if(!m){ return alert('Unable to process order') }
                if(clarity){ clarity.smartEvent('draft_match_order') }
                setDraftOrder({ visible: true, order:o, match: m });
                return
            default: return alert('Unable to process')
        }
    }


    const onSelectEvent = async(ev:EventProps, init_player_market?:MarketProps) => {
        MarketMakerApi.setEnvironment();
        setMarketData({ ...market_data, market_loading: true });
        const evs = await MarketComponentApi.getEventsByEventIds([ev.event_id]);
        const new_event = evs[0];
        const prices = await MarketMakerApi.getPricesByEvents('team', [ev.event_id]);
        const tds = await MarketComponentApi.getLatestTradesByEvents('team', [ev.event_id]);
        //Lets search for the best player prop market
        let pms = markets.filter(m => ev?.supported_markets?.map(sm => sm.market_id.toString()).includes(m.market_id.toString()) && m.level == 'athlete')
        let props =  MarketAppHelpers.getPropTradesForEvent(pms, tds, ev.event_id);
        let active_pm = pms.find(m => m.market_id == props[0]?.market_id);
        let unique_ids = [ ...new Set(tds.filter(p => p.side_type == 'athlete').map(p => p.side_id)) ]
        let needed_ids = unique_ids.filter(id => !athletes.map(a => a.athlete_id.toString()).includes(id.toString()));
        const aths = await MarketComponentApi.getAthletesByIds(needed_ids);

        setMarketData({ 
            ...market_data,
            external_prices: prices,
            latest_trades: tds,
            active_player_market: active_player_market ?? init_player_market ?? active_pm,
            athletes: athletes.filter(a => !aths.find(na => na.athlete_id == a.athlete_id)).concat(aths),
            market_loading: false
        });
        setNavState({
            level: 'event',
            event: new_event,
            team_id: init_team?.team_id,
            league: league ?? init_league
        })
    }

    const handleSelectTeam = async(team_id:string) => {

        setNavState({
            ...nav_state,
            level: 'team',
            team_id: team_id,
            event: undefined,
            tournament: undefined,
            athlete: undefined,
            match: undefined
        })
    }

     const renderAthleteProps = (data:{ item:TradeProps, index:number, compact?:boolean }) => {
        if(!event){ return <></> }
        const market = markets.find(m => m.market_id == data.item.market_id);
        if(!market){ return <></> }
        const athlete = athletes.find(a => a.athlete_id == data.item.side_id);
        if(!athlete){ return <></> }
        let best_available = best_available_orders[`team:${data.item.event_id}`] ?? []
        best_available = best_available.filter(a => a.market_id == data.item.market_id && a.side_type == 'athlete' && a.side_id == data.item.side_id);
        let event_trades = latest_trades.filter(t => t.event_id == event?.event_id);
        event_trades = event_trades.filter(a => a.market_id == data.item.market_id && a.side_type == 'athlete' && a.side_id == data.item.side_id);
        let order_stats = event_order_stats[`team:${data.item.event_id}`] ?? []
        order_stats = order_stats.filter(a => a.market_id == data.item.market_id && a.side_type == 'athlete' && a.side_id == data.item.side_id);
        return (
            <View key={data.item.trade_id.toString()} style={{ paddingLeft:5, paddingRight:5, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                <MarketComponents.AthleteMarket
                    event={event}
                    direction='horizontal'
                    image_size={40}
                    style={{ padding:0 }}
                    hide_stat_banner
                    best_available_orders={best_available}
                    event_order_stats={order_stats}
                    latest_trades={event_trades}
                    onOrder={(o) => handleDraftOrder(o, event)}
                    event_type='team'
                    onTradeLongPress={() => console.log('')}
                    onView={(data) => console.log(data)}
                    hide_liquidity
                    market={market}
                    default_price_view='best_available'
                    athlete={athlete}
                />
            </View>
            )   
        }
    

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'collapsed_schedule':
                if(!show_desktop_props){ return <></> }
                return (
                    <Button style={{ flexDirection:'row', alignItems:'center', padding:10}} onPress={() => setNavState({ ...nav_state, show_desktop_props: false })}>
                        <LinearDiagnal parent_style={{ flex:1 }} style={{ padding:10, flexDirection:'row', alignItems:'center' }} left_color={Colors.text.error} right_color={Colors.text.warning}>
                            <Icons.ScoreboardIcon size={18} color={Colors.text.white}/>
                            <Text theme='h1' style={{ marginLeft:10 }}>View Schedule</Text>
                        </LinearDiagnal>
                    </Button>
                )
            case 'team':
                if(nav_state.level != 'team' || !team_id){ return <></> }
                return (
                    <View>
                       <TeamCard
                            team_id={team_id}
                       />
                    </View>
                )
            case 'inspect':
                return (
                    <View style={{ flexDirection:'row' }}>
                        {!show_desktop_props ?
                        <View style={{ flex:1, minWidth:300, padding:10 }}>
                            <View style={{ flexDirection:'row', alignItems:'center', paddingRight:10}}>
                                <LinearDiagnal parent_style={{ flex:1 }} style={{ padding:14 }} label='Select League' left_color={Colors.text.warning} right_color={Colors.text.error} />
                                <Button float style={{ flexDirection:'row', alignItems:'center', padding:10 }} onPress={() => setContestState({ ...contest_state, league_selector: true })}>
                                    <Text theme='description' style={{ marginRight:5 }}>{league?.league_name}</Text>
                                    <Icons.ChevronIcon direction='down' size={8} color={Colors.text.h1} />
                                </Button>
                            </View>

                            {league ?
                            <LeagueSchedule
                                active_event={event?.event_id}
                                league={league}
                                onSelectEvent={(event) => onSelectEvent(event)}
                            />
                            :<></>}
                        </View>
                        :<></>}
                        <View transparent style={{ flex:2, minWidth:375, padding:10, borderLeftWidth:1, borderColor:Colors.borders.light }}>
                            {event && league ?
                            <LinearDiagnal style={{ padding:10, flexDirection:'row', alignItems:'center' }} left_color={Colors.text.bronze} right_color={Colors.text.gold}>
                                <Image
                                    source={{ uri: league.league_image }}
                                    style={{ height:30, width:30 }}
                                    resizeMode='contain'
                                />
                                <View transparent style={{ flex:1, marginLeft:10 }}>
                                    <Text theme='h2' color={Colors.text.white}>{event.event_title}</Text>
                                </View>
                            </LinearDiagnal>
                            :<></>}
                            <View style={{ padding:10, zIndex:1 }}>
                                {event ?
                                <TeamEventMarket
                                    event={event}
                                    latest_trades={latest_trades}
                                    best_available_orders={ev_available_orders}
                                    onOrder={(o) => handleDraftOrder(o, event)}
                                    float       
                                    show_help
                                    help_location='bottom'     
                                    hide_liquidity
                                    init_expanded={desktop_props && !show_desktop_props ? false : true}
                                    hide_non_primary={desktop_props && !show_desktop_props ? true : false}
                                    onTeamSelect={(team_id) => handleSelectTeam(team_id)}
                                    markets={markets}
                                    
                                    flash_markets={[]}
                                    onFlashSelect={() => console.log('')}
                                    event_order_stats={[]}
                                    default_price_view='best_available'
                                    onView={() => console.log('')}
                                    onCompetitionSelect={() => console.log('')}
                                    onSquaresSelect={() => console.log('')}
                                    onTradeLongPress={() => console.log('')}
                                    onViewAdditionalMarkets={() => {
                                        setNavState({
                                            ...nav_state,
                                            show_desktop_props: true
                                        });
                                    }}
                                />
                                : 
                                <Text theme='h1' style={{ alignSelf:'center' }} size={32}>SELECT AN EVENT</Text>
                                }
                            </View>
                            {event ?
                            <View style={{ flexDirection:'row', flexWrap:'wrap', padding:10, zIndex: -1 }}>
                                <View float style={{ flex:1, margin:3, minWidth:330 }}>
                                    <TeamFutureCard
                                        header_style={{ borderTopRightRadius:8, borderTopLeftRadius:8 }}
                                        team_id={event?.away_team_id}
                                        markets={markets}
                                        best_available_orders={best_available_orders}
                                        onOrder={(o, t) => handleDraftOrder(o, undefined, t)}
                                    />
                                </View>
                                <View float style={{ flex:1, margin:3, minWidth:330 }}>
                                    <TeamFutureCard
                                        header_style={{ borderTopRightRadius:8, borderTopLeftRadius:8 }}
                                        team_id={event?.home_team_id}
                                        markets={markets}
                                        best_available_orders={best_available_orders}
                                        onOrder={(o, t) => handleDraftOrder(o, undefined, t)}
                                    />
                                </View>
                            </View>
                            :<></>}
                            {market_loading ? 
                            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, paddingTop:50, alignItems:'center' }}>
                                <ActivityIndicator size={40} color={Colors.text.h1} />
                            </View>
                            :<></>}
                        </View>
                        {desktop_props || show_desktop_props ?
                        <View style={{ flex:1, minWidth:375, padding:10, borderLeftWidth:1, borderColor:Colors.borders.light }}>

                            <View type='header' style={{ flexDirection:'row', alignItems:'center', paddingRight:10 }}>
                                <LinearDiagnal parent_style={{ flex:1 }} style={{ padding:10, flexDirection:'row', alignItems:'center' }} left_color={Colors.absolutes.brand.midnightTopGradient} right_color={Colors.absolutes.brand.midnight}>
                                     <Icons.AthleteIcon size={26} color={Colors.text.white}/>
                                    <View transparent style={{ flex:1, marginLeft:10 }}>
                                        <Text theme='h2' color={Colors.text.white}>Player Markets</Text>
                                        <Text theme='description' color={Colors.text.white}  style={{ marginTop:3 }}>Select dropdown to change market</Text>
                                    </View>
                                </LinearDiagnal>
                               
                                <Button float style={{ padding:10 }}
                                    onPress={() => setSearchPlayers({ active:!search_players.active, value:'' })}
                                >
                                    {search_players.active ?
                                    <Icons.CloseIcon color={Colors.text.error} size={14} />
                                    :
                                    <Icons.SearchIcon color={Colors.text.h1} size={14} />
                                    }
                                </Button>
                                <DropDown
                                    dropdown_options={[
                                        { value: 'market', eligible_options: player_markets.map(pm => pm.stat_label ?? pm.stat) }
                                    ]}
                                    
                                    onOptionSelect={(stat:string) => {
                                        let selected = player_markets.find(m => m.stat_label == stat);
                                        if(!selected){ selected = player_markets.find(m => m.stat == stat) }
                                        if(!selected){ return }
                                        setMarketData({ ...market_data, active_player_market: selected })
                                    }}
                                    selected_value={active_player_market?.stat_label ?? active_player_market?.stat ?? ''}
                                />
                            </View>
                            {search_players.active ? 
                            <SearchBox
                                hide_search_button
                                onChange={(text) => setSearchPlayers({ ...search_players, active: true, value: text })}
                            />
                            :<></>}
                            <View style={{ flex:1 }}>
                                <FlatList
                                    data={active_player_props}
                                    key={'event_player_props'}
                                    keyExtractor={(item) => item.trade_id}
                                    renderItem={renderAthleteProps}
                                />
                            </View>
                            {market_loading ? 
                            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, paddingTop:50, alignItems:'center' }}>
                                <ActivityIndicator size={40} color={Colors.text.h1} />
                            </View>
                            :<></>}
                        </View>
                        :<></>}
                    </View>
                )
            default: return <></>
        }
    }

    const renderMobileSections = (data:{ item:string, index:number }) => {
        switch (data.item){
            case 'mobile_navigator':
                return (
                    <View style={{ flexDirection:'row', alignItems:'center', padding:5, zIndex: -1 * data.index }}>
                        {league ?
                        <Button style={{ padding:10 }} onPress={() => {
                            setNavState({
                                level: 'league',
                                league: league
                            });
                        }}>
                            <Text theme='action' size={12}>{league.league_name}</Text>
                        </Button>
                        :<></>}
                        {event ? 
                        <Icons.ChevronIcon size={8} direction='right' color={Colors.text.h1} />
                        :<></>}
                        {event ?
                        <Button style={{ padding:10 }} onPress={() => {
                            setNavState({
                                level: 'event',
                                league: league,
                                event: event
                            });
                        }}>
                            <Text theme='action' size={12}>{event.event_title}</Text>
                        </Button>
                        :<></>}
                        {level == 'athlete' ?
                         <Icons.ChevronIcon size={8} direction='right' color={Colors.text.h1} /> 
                        :<></>}
                        {level == 'athlete' ?
                        <Button style={{ padding:10 }} onPress={() => {
                            setNavState({
                                level: 'athlete',
                                league: league,
                                event: event
                            });
                        }}>
                            <Text theme='action' size={12}>Props</Text>
                        </Button>
                        :<></>}
                    </View>
                )
            case 'm_leagues':
                if(level != 'league'){ return <></> }
                return (
                    <View>
                        <View style={{ flexDirection:'row', alignItems:'center'}}>
                            <LinearDiagnal parent_style={{ flex:1 }} style={{ padding:14 }} label='Select League' left_color={Colors.text.warning} right_color={Colors.text.error} />
                            <Button float style={{ flexDirection:'row', alignItems:'center', padding:10 }} onPress={() => setContestState({ ...contest_state, league_selector: true })}>
                                <Text theme='description' style={{ marginRight:5 }}>{league?.league_name}</Text>
                                <Icons.ChevronIcon direction='down' size={8} color={Colors.text.h1} />
                            </Button>
                        </View>

                        {league ?
                        <LeagueSchedule
                            active_event={event?.event_id}
                            league={league}
                            onSelectEvent={(event) => onSelectEvent(event)}
                        />
                        :<></>}
                    </View>
                )
            case 'm_event':
                if(level != 'event'){ return <></> }
                if(!event){ return <></> }
                return (
                    <View transparent style={{ marginTop:10 }}>
                        <View style={{ flexDirection:'row', alignItems:'center'}}>
                            <LinearDiagnal parent_style={{ flex:1 }} style={{ padding:14 }} label='Event Details' left_color={Colors.absolutes.brand.midnightTopGradient} right_color={Colors.absolutes.brand.midnight} />
                        </View>
                        <View transparent style={{ padding:10 }}>
                        <TeamEventMarket
                                event={event}
                                latest_trades={latest_trades}
                                best_available_orders={ev_available_orders}
                                onOrder={(o) => handleDraftOrder(o, event)}
                                float
                                show_help
                                help_location='bottom'             
                                hide_liquidity
                                init_expanded={true}
                                hide_non_primary={false}
                                onTeamSelect={(team_id) => handleSelectTeam(team_id)}
                                markets={markets}
                                flash_markets={[]}
                                onFlashSelect={() => console.log('')}
                                event_order_stats={[]}
                                default_price_view='best_available'
                                onView={() => console.log('')}
                                onCompetitionSelect={() => console.log('')}
                                onSquaresSelect={() => console.log('')}
                                onTradeLongPress={() => console.log('')}
                                onViewAdditionalMarkets={() => {
                                    setNavState({
                                        ...nav_state,
                                        level: 'athlete'
                                    });
                                }}
                            />
                        </View>
                    </View>
                )
            case 'm_futures':
                if(level != 'event'){ return <></> }
                if(!event){ return <></> }
                return (
                    <View transparent style={{ marginTop:10 }}>
                        <View style={{ flexDirection:'row', alignItems:'center'}}>
                            <LinearDiagnal parent_style={{ flex:1 }} style={{ padding:14 }} label='Futures' left_color={Colors.text.bronze} right_color={Colors.text.gold} />
                        </View>
                         <View transparent>
                            <TeamFutureCard
                                team_id={event.away_team_id}
                                markets={markets}
                                best_available_orders={best_available_orders}
                                onOrder={(o, t) => handleDraftOrder(o, undefined, t)}
                            />
                        </View>
                        <View transparent style={{ marginTop:10 }}>
                            <TeamFutureCard
                                team_id={event.home_team_id}
                                markets={markets}
                                best_available_orders={best_available_orders}
                                onOrder={(o, t) => handleDraftOrder(o, undefined, t)}
                            />
                        </View>
                    </View>
                )
            case 'm_props':
                if(level != 'athlete'){ return <></> }
                if(!event){ return <></> }
                return (
                     <View style={{  zIndex: -1 * data.index }}>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <Icons.AthleteIcon size={26} color={Colors.text.action}/>
                            <View transparent style={{ flex:1, marginLeft:10 }}>
                                <Text theme='h2'>Player Markets</Text>
                                <Text theme='action' style={{ marginTop:3 }}>Select dropdown to change market</Text>
                            </View>
                            <Button float style={{ padding:10 }}
                                onPress={() => setSearchPlayers({ active:!search_players.active, value:'' })}
                            >
                                {search_players.active ?
                                <Icons.CloseIcon color={Colors.text.error} size={14} />
                                :
                                <Icons.SearchIcon color={Colors.text.h1} size={14} />
                                }
                            </Button>
                            <DropDown
                                dropdown_options={[
                                    { value: 'market', eligible_options: player_markets.map(pm => pm.stat_label ?? pm.stat) }
                                ]}
                                
                                onOptionSelect={(stat:string) => {
                                    let selected = player_markets.find(m => m.stat_label == stat);
                                    if(!selected){ selected = player_markets.find(m => m.stat == stat) }
                                    if(!selected){ return }
                                    setMarketData({ ...market_data, active_player_market: selected })
                                }}
                                selected_value={active_player_market?.stat_label ?? active_player_market?.stat ?? ''}
                            />
                        </View>
                        {search_players.active ? 
                        <SearchBox
                            hide_search_button
                            onChange={(text) => setSearchPlayers({ ...search_players, value: text })}
                        />
                        :<></>}
                        <View style={{ flex:1 }}>
                            <FlatList
                                data={active_player_props}
                                key={'event_player_props'}
                                keyExtractor={(item) => item.trade_id}
                                renderItem={renderAthleteProps}
                            />
                        </View>
                    </View>
                )
            default: return <></>
        }
    }
    return (
        <PortalModal>
        <Modal
            visible={visible}
            transparent
            style={{ flex:1 }}
            animationType={mode == 'desktop' ? 'fade' : 'slide'}
        >
            <TouchableWithoutFeedback disabled={mode == 'mobile' ? true : false} style={{ flex:1 }} onPress={() => onClose()}>
            <View type='blur' style={mode=='desktop'?{ flex:1, justifyContent:'center', alignItems:'center' }:{ flex:1 }}>
                <View float style={mode=='desktop'?{ height:embed_height - insets.bottom - insets.top - 10, width:embed_width - insets.left - insets.right }:{ flex:1, marginTop:insets.top + 10, marginBottom: insets.bottom + 10, marginLeft:insets.left + 10, marginRight: insets.right + 10  }}>
                    <View style={{ flex:1 }}>
                        <MarketHeader 
                            me={me}
                            my_balance={my_balance}
                            mode={mode}
                            company={company}
                            onShowProfile={() => {
                                onClose()
                                onShowProfile()
                            }}
                            onShowWallet={() => {
                                onClose()
                                onShowWallet()
                            }}
                            onRequestAuthenticate={() => onRequestAuthenticate()}
                        />
                        <View style={{ flex:1 }}>
                            {mode == 'desktop' ?
                            <FlatList
                                data={['collapsed_schedule','leagues','header','team','inspect']}
                                renderItem={renderSections}
                                key={'desktop_market_app_list'}
                                keyExtractor={item => item}
                            />
                            :
                            <FlatList
                                data={['mobile_navigator','m_leagues', 'm_event', 'm_futures','m_props']}
                                renderItem={renderMobileSections}
                                CellRendererComponent={({ index, children, style, ...rest }) => {
                                const z = 5 - index; // top item = highest z
                                return (
                                <View
                                    {...rest}
                                    style={[
                                    { position: 'relative' },
                                    { zIndex: z },
                                    Platform.OS === "android" && { elevation: z },
                                    ]}
                                    pointerEvents="box-none"
                                >
                                    {children}
                                </View>
                                );
                            }}
                                key={`mobile_market_app_list`}
                                keyExtractor={item => item}
                            />
                            }
                        </View>
                    </View>
                    {mode == 'mobile' ?
                    <View type='footer' style={{ flexDirection:'row', borderBottomRightRadius:8, borderBottomLeftRadius:8, padding:10, marginRight:5 }}>
                        <Button backgroundColor={Colors.absolutes.brand.electric} style={{ flex:1, flexDirection:'row', padding:10, alignItems:'center', justifyContent:'center' }} onPress={() => onNavOut()}>
                            <Icons.PhoneIcon size={14} color={Colors.text.white} />
                            <Text style={{ marginLeft:5 }} theme='h2' color={Colors.text.white}>Go to the app</Text>
                        </Button>
                        <Button
                            style={{ flex:2, marginLeft:5 }}
                            title='CLOSE'
                            type='error'
                            onPress={() => onClose()}
                        />
                    </View>
                    :
                        <View type='footer' style={{ flexDirection:'row', alignItems:'center', borderBottomRightRadius:8, borderBottomLeftRadius:8, padding:10 }}>
                            <Icons.BettorEdgeIcon size={16} color={Colors.text.h1} />
                            <View transparent style={{ flex:1, marginLeft:10 }}>
                                <Text theme='h1'>BettorEdge - Social Betting Marketplace</Text>
                                <Text theme='description' style={{ marginTop:5 }}>All lines are generated by trades made on Bettoredge</Text>
                            </View>
                            <Button backgroundColor={Colors.absolutes.brand.electric} style={{ flexDirection:'row', padding:10, alignItems:'center' }} onPress={() => onNavOut()}>
                                <Icons.PhoneIcon size={14} color={Colors.text.white} />
                                <Text style={{ marginLeft:5 }} theme='h2' color={Colors.text.white}>Go to the app</Text>
                            </Button>
                        </View>
                    }
                </View>
            </View>
            </TouchableWithoutFeedback>
            <LeagueSelector
                leagues={leagues}
                hide_other
                insets={insets}
                active_league={league?.league_name}
                mode={mode}
                visible={league_selector}
                onLeagueSelect={(league) => {
                    let league_obj = leagues.find(l => l.league_name == league);
                    if(!league_obj){ return }
                    setContestState({ ...contest_state, league_selector: false })
                    setNavState({ ...nav_state, league: league_obj, event: undefined })
                }}
                onClose={() => setContestState({ ...contest_state, league_selector: false })}
            />
            {draft_order?.order ?
            <OrderHolder
                me={me}
                my_balance={my_balance}
                onRequestAuthenticate={onRequestAuthenticate}
                onRequestLocation={() => {
                    setDraftOrder({ ...draft_order, visible: false })
                    setLocation({ ...location_state, visible: true })
                    if(clarity){ clarity.smartEvent('show_location') }
                }}
                location={location_state.location}
                visible={draft_order.visible}
                order={draft_order.order}
                event={draft_order.event}
                tournament={draft_order.tournament}
                match={draft_order.match}
                markets={markets}
                mode={mode}
                onClose={() => setDraftOrder({ visible: false })}
                onCreatedOrder={(o) => console.log(o)}
            />
            :<></>}
            <LocationHolder
                player_id={me?.player_id}
                onClose={() => {
                    setLocation({ ...location_state, visible:false });
                    if(draft_order.order){ setDraftOrder({ ...draft_order, visible: true }) }
                }}
                visible={location_state.visible}
                mode={mode}
                onLocationUpdate={(location) => {
                    if(!location_state.visible){ return }
                    let visible = true
                    if(location?.location?.legal_ind && location_state.visible){ visible = false }
                    setLocation({
                        visible,
                        location: {
                            location: location.location,
                            region: location.region,
                            coordinates: location.coordinates
                        }
                    });
                    if(!visible && draft_order.order){ setDraftOrder({ ...draft_order, visible: true }) }
                }}
            />
        </Modal>
        </PortalModal>
    )
}

export default MarketApp


const styles = StyleSheet.create({
  cell: {
    // zIndex on Android behaves best when the view is positioned
    position: "relative",
  }
});